/**
 * ProjectDetailsHeader Component
 *
 * Renders the header section of the project details view including:
 * - Back navigation button
 * - Project title
 * - Owner info
 * - Project description
 * - Status selector
 * - Settings button
 *
 * Responsive: Stacks vertically on mobile/tablet
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from 'antd';
import { LeftOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import ProjectDescription from './ProjectDescription';
import '../../styles/index.scss';

const { Option } = Select;

const ProjectDetailsHeader = ({
  project,
  onBack,
  onStatusUpdate,
  onSettingsClick,
  updateLoading,
  isMobile,
  isTablet,
}) => {
  return (
    <div className="project-header">
      {/* Back Button */}
      <div className="project-header__back">
        <Button
          className="project-header__back-btn"
          onClick={onBack}
          type="text"
        >
          <LeftOutlined />
          <Text strong>Back</Text>
        </Button>
      </div>

      {/* Header Row */}
      <div className={`project-header__row ${isMobile || isTablet ? 'project-header__row--stacked' : ''}`}>
        {/* Title Section */}
        <div className="project-header__title-section">
          <h1 className="project-header__title">
            {project?.name || 'Untitled Project'}
          </h1>
          {project?.owner && (
            <div className="project-header__owner">
              <UserOutlined className="project-header__owner-icon" />
              <span className="project-header__owner-name">
                {startCase(project.owner.name || project.owner.username)}
              </span>
            </div>
          )}
          <ProjectDescription
            description={project?.description}
            onAddDescription={onSettingsClick}
          />
        </div>

        {/* Actions */}
        <div className={`project-header__actions ${isMobile || isTablet ? 'project-header__actions--full' : ''}`}>
          <Select
            className="project-header__status-select"
            value={project?.status}
            placeholder="Update Status"
            onChange={onStatusUpdate}
            loading={updateLoading}
            disabled={updateLoading}
          >
            <Option value="NOT_STARTED">Not Started</Option>
            <Option value="PLANNING">Planning</Option>
            <Option value="IN_PROGRESS">In Progress</Option>
            <Option value="COMPLETED">Completed</Option>
          </Select>

          <Button
            className="project-header__settings-btn"
            onClick={onSettingsClick}
          >
            <SettingOutlined />
            {!isMobile && <span>Settings</span>}
          </Button>
        </div>
      </div>
    </div>
  );
};

ProjectDetailsHeader.propTypes = {
  project: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    owner: PropTypes.shape({
      name: PropTypes.string,
      username: PropTypes.string,
    }),
  }),
  onBack: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  updateLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
};

ProjectDetailsHeader.defaultProps = {
  project: null,
  updateLoading: false,
  isMobile: false,
  isTablet: false,
};

export default ProjectDetailsHeader;
